import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { interval } from 'rxjs';
import { isAfter, isSameDay, isSameMonth, parseISO, startOfDay } from 'date-fns';

import { Orders, ProgressObjective } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { Log } from 'src/app/services/shared/log';
import { timer } from 'rxjs';

/** Shape of `/assets/config.json` — only the field we read. */
interface IWarehouseConfig {
  warehouseTimer?: number;
}

/**
 * Warehouse display page (route: /warehouseorders).
 *
 * This page is rendered on a TV mounted ~4m above the warehouse floor.
 * The audience is the operators on shift, who read it from distance and
 * cannot touch it. The page is therefore PASSIVE: no click handlers, no
 * navigation, no search. It only displays:
 *   - the live clock + brand,
 *   - today's daily/monthly objective progress (sticky header),
 *   - orders currently in progress,
 *   - orders waiting for signature (today),
 *   - orders closed today.
 *
 * Data is polled every `warehouseTimer` milliseconds (configured in
 * `/assets/config.json`; default 2 minutes if the config is missing).
 *
 * Architectural notes / Phase 1 candidates:
 *  - `OrdersService` is the legacy fan-out for everything orders.
 *    Extracting a `WarehouseDisplayService` with a single facade
 *    (one observable per section + a shared refresh trigger) would
 *    halve the moving parts in this file.
 *  - When the codebase moves to standalone components + signals, the
 *    four imperative subscribes here become four `toSignal()` calls and
 *    the polling becomes an RxJS pipeline keyed off a `timer` signal.
 */
@Component({
  selector: 'app-warehouse-orders',
  templateUrl: './warehouse-orders.component.html',
  styleUrls: ['./warehouse-orders.component.scss'],
  standalone: false,
})


export class WarehouseOrdersComponent implements OnInit {

  private readonly ordersService = inject(OrdersService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Polling interval per il TV monitor magazzino.
   *
   * 🚨 v2.12.8: ridotto da 60s → 30s dopo feedback PM. Il monitor
   * è appeso a 4m di altezza in magazzino; quando un ordine passa
   * da "in lavorazione" → "da firmare" gli operatori vogliono vedere
   * la progressbar KPI (giornaliera/mensile) aggiornarsi entro pochi
   * secondi, non 1 minuto. 30s è compromesso sano tra latenza
   * percepita e carico BE su singolo endpoint.
   *
   * La chiamata è idempotente lato BE (getObjectiveProgress) — non
   * mette pressione né sull'app né sul DB. Se il PM chiede
   * ancora più responsivo, si può bumpare a 15s senza rischi.
   */
  private static readonly DEFAULT_REFRESH_MS = 30 * 1000;

  /** Live wall-clock — driven by an `interval(1000)` in `ngOnInit`. */
  now: Date = new Date();

  /** Orders currently in the pipeline (anything not in `Assegnato` state). */
  openOrders: Orders.IOrderOpenList[] = [];

  /**
   * Orders currently in "to-sign" status (unsigned by the carrier).
   * Pre-filtered to `or_assigned_date <= today` (accumulate unsigns of
   * previous days too). Since v2.12.9 this raw list also drives:
   *   - the carrier breakdown widget (T2b)
   *   - the "prepared today/month" semantic (T2c)
   * so we keep the accumulated view here and derive daily/monthly
   * splits via getters below.
   */
  ordersToSign: Orders.OrderSignature[] = [];

  /**
   * All closed orders returned by `getClosedOrders()`. Kept raw here
   * (post-v2.12.9 T2c refactor) so we can derive both "closed today"
   * and "closed this month" via getters — needed to compute the
   * monthly progressbar with the new "preparato = concluso" semantic.
   */
  private closedOrdersRaw: Orders.IOrderClosed[] = [];

  /**
   * 🚨 Fix v2.13.1 W1 (2026-07-09): memoization dei valori €.
   *
   * Bug osservato: le card "Corrieri in attesa" mostravano sempre 0€
   * anche dopo il fix v2.12.9 T2c (join con openOrders). Root cause:
   * quando un ordine passa da "In lavorazione" → "Da firmare", il BE
   * lo esclude da `getOpenOrders()` — quindi `openOrdersCache` non
   * ha più il record, il match `order_id` fallisce, orderValueOf
   * ritorna 0. Le card operatori funzionavano perché iterano su
   * `closedOrdersToday` (or_net_value popolato per gli ordini chiusi).
   *
   * Fix: mantenere una `Map` che memorizza tutti i valori € visti
   * per `order_id`, popolata ad ogni polling da `openOrders` e
   * `closedOrdersRaw`. Al lookup, prima cerchiamo nel memo — anche
   * se l'ordine è sparito da openOrders, il valore memorizzato
   * quando era ancora "In lavorazione" resta disponibile.
   *
   * La memo persiste per l'intera sessione del monitor (TV 4m,
   * refresh raro). Se un ordine cambia valore lato BE, il memo
   * verrà aggiornato al prossimo poll (overwrite con nuovo valore).
   */
  private orderValueMemo = new Map<number | string, number>();

  /** Daily + monthly progress (single source of truth for the objectives). */
  progress: ProgressObjective | null = null;

  /** True while the progress endpoint is in flight (drives the skeleton). */
  loadingProgress = false;

  /** Polling interval in milliseconds — read from config at boot. */
  private refreshIntervalMs = WarehouseOrdersComponent.DEFAULT_REFRESH_MS;

  ngOnInit(): void {
    this.startLiveClock();
    this.loadConfigThenStartPolling();
    //this.refreshAll();
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state (kept as getters so the template stays declarative).
  // ──────────────────────────────────────────────────────────────────

  // ─── Closed orders splits (from `closedOrdersRaw`) ──────────────────
  /** Orders whose `or_closed_date` falls on today. */
  get closedOrdersToday(): Orders.IOrderClosed[] {
    const today = startOfDay(new Date());
    return this.closedOrdersRaw.filter((o) => {
      if (!o?.or_closed_date) return false;
      return isSameDay(parseISO(o.or_closed_date), today);
    });
  }

  /** Orders whose `or_closed_date` falls in the current calendar month. */
  get closedOrdersMonth(): Orders.IOrderClosed[] {
    const now = new Date();
    return this.closedOrdersRaw.filter((o) => {
      if (!o?.or_closed_date) return false;
      return isSameMonth(parseISO(o.or_closed_date), now);
    });
  }

  /**
   * Fix 2026-07-08: risolve il valore € di un ordine "to sign".
   *
   * `getOrdersToSign()` restituisce sempre `or_net_value = 0` (bug BE:
   * il campo non viene calcolato per gli ordini in attesa firma). Ma
   * lo stesso ordine è presente anche in `openOrders` (endpoint
   * `getOpenOrders`, che restituisce TUTTI gli ordini != "Assegnato"
   * inclusi quelli in stato "Da firmare") — e lì `or_net_value` è
   * correttamente valorizzato (visibile in pagina "Preparazione Ordini").
   *
   * Priorità di lookup:
   *   1. `order.or_net_value` se > 0 (best case, no join needed)
   *   2. Match in `openOrders` per `order_id` (fix principale)
   *   3. Match in `closedOrdersRaw` per `order_id` (safety net)
   *   4. Fallback 0
   *
   * Se in futuro il BE espone `or_net_value` anche su `to-sign`, questo
   * helper diventa un no-op transparente (step 1 vince sempre).
   */
  private orderValueOf(order: { order_id?: number | string; or_net_value?: number | string | null }): number {
    // 1. Se l'ordine ha già or_net_value valido, usalo e memoizza.
    const direct = typeof order?.or_net_value === 'string'
      ? Number(order.or_net_value)
      : (order?.or_net_value ?? 0);
    if (Number.isFinite(direct) && direct !== 0) {
      if (order?.order_id !== undefined && order?.order_id !== null) {
        this.orderValueMemo.set(order.order_id, direct as number);
      }
      return direct as number;
    }

    const oid = order?.order_id;
    if (oid === undefined || oid === null) return 0;

    // 2. Cache memo (fix v2.13.1 W1: gli ordini "Da firmare" possono
    // essere sparire da openOrdersCache dopo la preparazione — la
    // memo preserva il valore visto quando erano "In lavorazione").
    const memoed = this.orderValueMemo.get(oid);
    if (memoed && memoed > 0) return memoed;

    // 3. Match live in openOrders (poll corrente).
    const openMatch = this.openOrders.find((o) => o.order_id === oid);
    if (openMatch?.or_net_value) {
      const v = Number(openMatch.or_net_value) || 0;
      if (v > 0) this.orderValueMemo.set(oid, v);
      return v;
    }

    // 4. Match live in closedOrdersRaw.
    const closedMatch = this.closedOrdersRaw.find((o) => o.order_id === oid);
    if (closedMatch?.or_net_value) {
      const v = Number(closedMatch.or_net_value) || 0;
      if (v > 0) this.orderValueMemo.set(oid, v);
      return v;
    }

    return 0;
  }

  // ─── "Preparato oggi/questo mese" (v2.12.9 T2c) ─────────────────────
  /**
   * Semantic: **ordine preparato = ordine uscito da "in lavorazione"**.
   * Include sia gli ordini in attesa firma (già preparati, non ancora
   * firmati dal corriere) sia quelli già chiusi (firmati). Il PM ha
   * chiesto questa semantica perché i corrieri passano tipicamente a
   * fine giornata → contare solo i "chiusi" faceva vedere la KPI
   * quasi a zero per tutta la giornata, demotivando gli operatori.
   *
   * ⚠️ Limitazione: usiamo `or_assigned_date` come proxy per "data
   * preparazione" — nella maggior parte dei casi coincidono (ordine
   * assegnato oggi → preparato oggi), ma se un ordine è assegnato
   * ieri e preparato oggi la data proxy lo classifica come "ieri".
   * Fix pulito richiede un campo BE `or_prepared_date` dedicato.
   * Segnalato in CHANGELOG per iterazione futura.
   */
  private get ordersToSignPreparedToday(): Orders.OrderSignature[] {
    const today = startOfDay(new Date());
    return this.ordersToSign.filter((o) => {
      if (!o?.or_assigned_date) return false;
      return isSameDay(parseISO(String(o.or_assigned_date)), today);
    });
  }

  private get ordersToSignPreparedThisMonth(): Orders.OrderSignature[] {
    const now = new Date();
    return this.ordersToSign.filter((o) => {
      if (!o?.or_assigned_date) return false;
      return isSameMonth(parseISO(String(o.or_assigned_date)), now);
    });
  }

  /** Count of orders "prepared today" = to-sign(assigned today) + closed today. */
  get preparedTodayCount(): number {
    return this.ordersToSignPreparedToday.length + this.closedOrdersToday.length;
  }

  /** Sum of `or_net_value` for orders "prepared today". */
  get preparedTodayValue(): number {
    // v2.12.9 T2c originale usava `o.or_net_value ?? 0` — ma BE
    // restituisce 0 per ordini to-sign (fix 2026-07-08). Ora
    // deleghiamo a orderValueOf() che joina con openOrders/closedRaw.
    const signSum = this.ordersToSignPreparedToday.reduce(
      (s, o) => s + this.orderValueOf(o),
      0,
    );
    const closedSum = this.closedOrdersToday.reduce(
      (s, o) => s + this.orderValueOf(o),
      0,
    );
    return signSum + closedSum;
  }

  /** Sum of `or_net_value` for orders "prepared this month". */
  get preparedMonthValue(): number {
    // Vedi commento in preparedTodayValue — stesso pattern.
    const signSum = this.ordersToSignPreparedThisMonth.reduce(
      (s, o) => s + this.orderValueOf(o),
      0,
    );
    const closedSum = this.closedOrdersMonth.reduce(
      (s, o) => s + this.orderValueOf(o),
      0,
    );
    return signSum + closedSum;
  }

  // ─── Progressbar effective values (v2.12.9 T2c + v2.13.1 W2) ────────
  /**
   * Target giornaliero DINAMICO (fix v2.13.1 W2).
   *
   * Pre-fix: usava `progress.daily_total` = calcolo BE-statico
   * (monthly / totale giorni lavorativi del mese, calcolato una volta
   * a inizio mese). Non rifletteva il ricalibramento day-by-day che
   * il PM ha chiesto: se ieri gli operatori hanno superato il
   * giornaliero, oggi deve essere più basso; se sono sotto, deve
   * salire per recuperare.
   *
   * Formula corretta (matematicamente equivalente al PM ragionamento):
   *   dailyTarget = (monthlyTotal - accumulatedEarnedMonth) / remainingWorkdays
   *
   * Dove:
   *   - monthlyTotal → progress.monthly_total (obiettivo mese, BE)
   *   - accumulatedEarnedMonth → preparedMonthValue (già computed,
   *     include to-sign + closed del mese)
   *   - remainingWorkdays → giorni lavorativi da oggi (incluso) a
   *     fine mese
   *
   * Edge case:
   *   - Se accumulated ≥ monthlyTotal (obiettivo mensile già superato)
   *     → target giornaliero = 0 (nulla da recuperare)
   *   - Se remainingWorkdays = 0 (ultimo giorno del mese, weekend)
   *     → target = remaining (tutto quello che manca, oggi)
   */
  get effectiveDailyTotal(): number {
    const monthlyTotal = this.toNumber(this.progress?.monthly_total);
    if (monthlyTotal <= 0) return this.toNumber(this.progress?.daily_total);

    // 🚨 Fix v2.13.1 (feedback PM 2026-07-09): usare accumulated
    // PRIMA di oggi, non il totale mensile. Così il target daily
    // resta STABILE durante il turno (non scende ad ogni ordine
    // chiuso in giornata) — ricalibrato solo la mattina dopo,
    // quando `preparedTodayValue` reset a 0 al cambio data e
    // ciò che è stato fatto oggi entra in `accumulatedBeforeToday`.
    //
    // Esempio: monthly=12567, ieri sera accumulated=4385, oggi target
    // = (12567-4385)/12 = 682. Se oggi faccio altri 500€, il target
    // di oggi RESTA 682 (non diventa 665). Domani mattina, senza
    // wait, il target diventa (12567-4885)/11 = 698 — ricalibrato
    // sull'accumulated di ieri sera+oggi.
    const accumulatedBeforeToday = Math.max(
      0,
      this.preparedMonthValue - this.preparedTodayValue,
    );
    const remaining = Math.max(0, monthlyTotal - accumulatedBeforeToday);
    const remainingWorkdays = this.remainingWorkdaysInMonth();

    if (remainingWorkdays <= 0) return remaining;
    return Math.max(0, remaining / remainingWorkdays);
  }

  /**
   * Percentuale > 100 = obiettivo giornaliero superato (celebration).
   * Il template applica classe `objective__bar--exceeded` che gli
   * dà glow verde/oro invece del semplice `--full`.
   */
  get isDailyExceeded(): boolean {
    return this.effectiveDailyPercentage > 100;
  }

  get isMonthlyExceeded(): boolean {
    return this.effectiveMonthlyPercentage > 100;
  }

  /**
   * Giorni lavorativi (lun-ven) rimanenti nel mese corrente, oggi
   * INCLUSO. Se oggi è sabato/domenica, non si conta oggi ma solo
   * i weekday che restano. Se oggi è lunedì primo del mese, includere
   * tutti i lun-ven del mese.
   */
  private remainingWorkdaysInMonth(): number {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    let count = 0;
    for (let day = today.getDate(); day <= lastDayOfMonth; day++) {
      const dow = new Date(year, month, day).getDay();
      if (dow !== 0 && dow !== 6) count++;
    }
    return count;
  }

  /** Progress giornaliero client-side = valore preparato oggi. */
  get effectiveDailyProgress(): number {
    return this.preparedTodayValue;
  }

  /** Percentuale giornaliera ricalcolata dalla nuova coppia progress/total. */
  get effectiveDailyPercentage(): number {
    if (!this.effectiveDailyTotal || this.effectiveDailyTotal <= 0) return 0;
    return (this.effectiveDailyProgress / this.effectiveDailyTotal) * 100;
  }

  get effectiveMonthlyTotal(): number {
    return this.toNumber(this.progress?.monthly_total);
  }

  get effectiveMonthlyProgress(): number {
    return this.preparedMonthValue;
  }

  get effectiveMonthlyPercentage(): number {
    if (!this.effectiveMonthlyTotal || this.effectiveMonthlyTotal <= 0) return 0;
    return (this.effectiveMonthlyProgress / this.effectiveMonthlyTotal) * 100;
  }

  // ─── Carrier breakdown (v2.12.9 T2b) ────────────────────────────────
  /**
   * Aggregate degli ordini attualmente in attesa firma, raggruppati
   * per corriere. Sostituisce la lista dettagliata "Da firmare oggi"
   * (nascosta in T2a) sul TV monitor: gli operatori vedono a colpo
   * d'occhio quanti pacchi ha ciascun corriere quando passa a ritirare.
   *
   * Fallback: se `or_carrier_desc` è nullo (ordine con corriere
   * manuale) usiamo `or_carrier_other`; se anche quello manca,
   * label generico "Non specificato" per non escludere l'ordine
   * dal conteggio.
   */
  /**
   * oggi" flat con card per operatore (stesso stile arancione dei
   * corrieri, tinta verde per successo). PM: dare visibilità
   * immediata a chi ha chiuso quanti ordini oggi = gratifica morale
   * per gli operatori.
   *
   * Fallback: se `ac_first_name`/`ac_last_name` sono nulli (rarissimo,
   * ordini importati senza operatore) label "Non specificato".
   *
   * Ordinamento: count desc — chi ha chiuso di più in cima.
   */
  get operatorClosedBreakdown(): Array<{ name: string; count: number; value: number }> {
    const map = new Map<string, { count: number; value: number }>();
    for (const order of this.closedOrdersToday) {
      const first = (order.ac_first_name ?? '').trim();
      const last = (order.ac_last_name ?? '').trim();
      const name = `${first} ${last}`.trim() || 'Non specificato';
      const acc = map.get(name) ?? { count: 0, value: 0 };
      acc.count += 1;
      acc.value += this.orderValueOf(order);
      map.set(name, acc);
    }
    // Fix v2.13.1 (feedback PM 2026-07-09): sort by VALUE desc (era
    // count desc). Il template applica medaglia 🥇/🥈/🥈 sulle prime
    // 3 card per un tocco motivazionale — ranking basato su fatturato
    // generato oggi, non su numero di ordini (un ordine grosso vale
    // più di 3 piccoli).
    return Array.from(map.entries())
      .map(([name, { count, value }]) => ({ name, count, value }))
      .sort((a, b) => b.value - a.value);
  }

  get carrierBreakdown(): Array<{ name: string; count: number; value: number }> {
    const map = new Map<string, { count: number; value: number }>();
    for (const order of this.ordersToSign) {
      const name =
        (order.or_carrier_desc && order.or_carrier_desc.trim()) ||
        (order.or_carrier_other && order.or_carrier_other.trim()) ||
        'Non specificato';
      const acc = map.get(name) ?? { count: 0, value: 0 };
      acc.count += 1;
      // Fix 2026-07-08: `order.or_net_value` è 0 dal BE per to-sign.
      // orderValueOf() joina con openOrders/closedRaw + memoization
      // (v2.13.1 W1) per recuperare il valore reale.
      acc.value += this.orderValueOf(order);
      map.set(name, acc);
    }
    return Array.from(map.entries())
      .map(([name, { count, value }]) => ({ name, count, value }))
      .sort((a, b) => b.count - a.count);
  }

  /** Sum of `or_net_value` across orders closed today. */
  get totalNetClosedToday(): number {
    return this.closedOrdersToday.reduce(
      (sum, order) => sum + (order.or_net_value ?? 0),
      0,
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Template helpers.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Coerce a possibly-string percentage ("75.43") into a finite number
   * for `kendo-progressbar`. Returns 0 for null / NaN.
   */
  toNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined) return 0;
    const n = typeof value === 'string' ? Number(value) : value;
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * Format a percentage for display: one decimal place, "-" when unknown.
   */
  formatPercentage(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    const n = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(n)) return '-';
    return n.toFixed(1);
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle helpers.
  // ──────────────────────────────────────────────────────────────────

  private startLiveClock(): void {
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.now = new Date();
        this.cdr.detectChanges();
      });
  }

  private loadConfigThenStartPolling(): void {
    this.refreshIntervalMs =
      WarehouseOrdersComponent.DEFAULT_REFRESH_MS;
    this.startPolling();
  }

  private startPolling(): void {
  timer(0, this.refreshIntervalMs)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.refreshAll();
      this.cdr.detectChanges();
    });
}

  /** Re-fetch every section together — one polling tick = one refresh. */
  private refreshAll(): void {
    this.loadOpenOrders();
    this.loadOrdersToSign();
    this.loadClosedOrders();
    this.loadObjectiveProgress();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadOpenOrders(): void {
    this.ordersService
      .getOpenOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orders: Orders.IOrderOpenList[]) => {
        Log.debug('warehouse loadOpenOrders raw', orders);
        this.openOrders = (orders ?? []).filter(
          (order) => order.tab_order_status_desc !== 'Assegnato',
        );
        this.cdr.detectChanges();
      });
  }

  private loadOrdersToSign(): void {
    this.ordersService
      .getOrdersToSign()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((orders: Orders.OrderSignature[]) => {
        Log.debug('warehouse loadOrdersToSign raw', orders);
        const today = startOfDay(new Date());
        this.ordersToSign = (orders ?? []).filter((order) => {
          if (!order?.or_assigned_date) return false;
          // Show unsigned orders assigned today OR any previous day
          // (accumulate unsigns). Since v2.12.9 T2b/T2c this list is
          // consumed by the carrier breakdown widget + the
          // "prepared today/month" getters — kept accumulated on
          // purpose (per-day splits live in the getters above).
          const assignedDay = startOfDay(parseISO(String(order.or_assigned_date)));
          return !isAfter(assignedDay, today);
        });
        this.cdr.detectChanges();
      });
  }

  private loadClosedOrders(): void {
    this.ordersService
      .getClosedOrders()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders: Orders.IOrderClosed[]) => {
          Log.debug('warehouse loadClosedOrders raw', orders);
          // v2.12.9 T2c: raw list, splits derived via `closedOrdersToday`
          // and `closedOrdersMonth` getters. Needed to feed both the
          // "prepared today" and "prepared this month" semantics.
          this.closedOrdersRaw = orders ?? [];
          this.cdr.detectChanges();
        },
        error: (err) => Log.warn('warehouse loadClosedOrders error', err),
      });
  }

  private loadObjectiveProgress(): void {
    this.loadingProgress = true;
    this.ordersService
      .getObjectiveProgress()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          Log.debug('warehouse loadObjectiveProgress raw', response);
          this.loadingProgress = false;
          this.progress =
            response?.success && response.data
              ? new ProgressObjective(response.data)
              : null;
          Log.debug('warehouse progress set to', this.progress);
          this.cdr.detectChanges();
        },
        error: (err) => {
          Log.warn('warehouse loadObjectiveProgress error', err);
          this.loadingProgress = false;
          this.progress = null;
        },
      });
  }
  
}
